import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type {
  AssistantDecisionLogRecord,
  AssistantLogListResponse,
  AssistantTurnLogRecord,
} from '@cumpli/shared';
import { ArrowLeft, Bot, MessagesSquare, Search, User, Wrench } from 'lucide-react';
import { api } from '../lib/api';
import { useTenants, formatDate } from '../lib/hooks';
import { Button, Card, EmptyState, PageHeader, Pill, Skeleton } from '../components/ui';
import { ChatMarkdown } from '../components/assistant/markdown';
import {
  buildConversations,
  hasActiveFilters,
  MAX_SEARCH_RECORDS,
  shouldAutoLoad,
  type AssistantLogFilters,
} from '../lib/assistant-log-search';

/**
 * "Conversaciones de IA" — supervisory read of the AI decision log,
 * super_admin ONLY (route guarded in App.tsx; server enforces
 * `assistant_log.read`). Master–detail review: a filterable conversation list
 * (who asked, when, how many turns, whether actions were confirmed) and a
 * chat-style replay of the selected thread. User names come resolved
 * server-side on the response (`users` directory).
 *
 * Search is a capped auto-drain over the whole tenant history: while any
 * filter is active, the page keeps fetching pages (via the same
 * api.listAssistantLog accumulator "Cargar más" uses) until it either runs
 * out of pages or hits MAX_SEARCH_RECORDS — so a match on an unloaded page
 * can never be reported as "no results" (see lib/assistant-log-search.ts).
 */

export default function ConversacionesIA() {
  const { t } = useTranslation();
  const { data: tenants } = useTenants();
  const [tenantId, setTenantId] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [extra, setExtra] = useState<AssistantLogListResponse | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Set when the auto-drain hits a fetch error — stops the retry loop instead
  // of hammering the API; the manual "Cargar más" button stays available.
  const [drainFailed, setDrainFailed] = useState(false);

  // Filters
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [onlyDecisions, setOnlyDecisions] = useState(false);

  // A new search or tenant switch gets a fresh chance to drain.
  useEffect(() => {
    setDrainFailed(false);
  }, [q, from, to, userFilter, onlyDecisions, tenantId]);

  // One real tenant is the common case — select it without ceremony.
  useEffect(() => {
    if (tenantId === '' && tenants && tenants.length > 0) setTenantId(tenants[0]?.id ?? '');
  }, [tenants, tenantId]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['assistant-log', tenantId],
    queryFn: () => api.listAssistantLog({ tenantId, limit: 200 }),
    enabled: tenantId !== '',
  });

  // Accumulated page set (base query + "Cargar más" pages).
  const all = useMemo(() => {
    const items = [...(data?.items ?? []), ...(extra?.items ?? [])];
    const users = { ...(data?.users ?? {}), ...(extra?.users ?? {}) };
    const nextCursor = extra ? extra.nextCursor : (data?.nextCursor ?? null);
    return { items, users, nextCursor };
  }, [data, extra]);

  const loadMore = async () => {
    if (!all.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await api.listAssistantLog({ tenantId, limit: 200, cursor: all.nextCursor });
      setExtra((prev) => ({
        items: [...(prev?.items ?? []), ...page.items],
        users: { ...(prev?.users ?? {}), ...page.users },
        nextCursor: page.nextCursor,
      }));
      // A successful manual load clears the failure notice and re-arms the auto-drain.
      setDrainFailed(false);
    } catch {
      // Same failure state as the auto-drain: stop retrying, show the notice,
      // keep the manual "Cargar más" button available.
      setDrainFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const userName = (id: string) => all.users[id]?.fullName ?? `${id.slice(0, 8)}…`;

  const filters: AssistantLogFilters = useMemo(
    () => ({ q, from, to, userId: userFilter, onlyDecisions }),
    [q, from, to, userFilter, onlyDecisions]
  );

  const conversations = useMemo(() => buildConversations(all.items, filters), [all, filters]);

  const filtersActive = hasActiveFilters(filters);
  // Mid-drain: filters are on, more history remains to check, and we have not
  // given up — the empty state must NOT render yet or a match on an
  // unloaded page would be invisibly missed.
  const searching =
    filtersActive && all.nextCursor !== null && all.items.length < MAX_SEARCH_RECORDS && !drainFailed;
  const capped = filtersActive && all.nextCursor !== null && all.items.length >= MAX_SEARCH_RECORDS;

  // Self-stepping auto-drain: while a filter is active and history remains,
  // fetch the next page and append it — this effect re-fires as
  // all.nextCursor / all.items.length advance, walking the whole tenant
  // history (capped at MAX_SEARCH_RECORDS) before the search is allowed to
  // report empty. Cancelled-flag cleanup (not a run-once ref) so React
  // StrictMode's double-invoke in dev cannot leak a stale in-flight append.
  useEffect(() => {
    if (loadingMore || drainFailed) return;
    if (!shouldAutoLoad(filters, all.nextCursor, all.items.length)) return;
    const cursor = all.nextCursor as string;
    let cancelled = false;
    void (async () => {
      try {
        const page = await api.listAssistantLog({ tenantId, limit: 200, cursor });
        if (cancelled) return;
        setExtra((prev) => ({
          items: [...(prev?.items ?? []), ...page.items],
          users: { ...(prev?.users ?? {}), ...page.users },
          nextCursor: page.nextCursor,
        }));
      } catch {
        if (!cancelled) setDrainFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q, from, to, userFilter, onlyDecisions, all.nextCursor, all.items.length, loadingMore, drainFailed, tenantId]);

  const current = conversations.find((c) => c.id === selected) ?? null;
  const knownUsers = useMemo(
    () => Object.entries(all.users).map(([id, u]) => ({ id, name: u.fullName })),
    [all.users]
  );

  const inputCn =
    'rounded-xl border border-line-strong bg-panel px-3 py-2 text-sm text-ink outline-none focus:border-primary';

  return (
    <div>
      <PageHeader title={t('aiLog.title')} subtitle={t('aiLog.subtitle')} />

      {/* Tenant: pills (no ceremony when there is only one). */}
      {(tenants?.length ?? 0) > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-faint">
            {t('aiLog.tenantLabel')}
          </span>
          {(tenants ?? []).map((tn) => (
            <button
              key={tn.id}
              type="button"
              onClick={() => {
                setTenantId(tn.id);
                setSelected(null);
                setExtra(null);
              }}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                tenantId === tn.id
                  ? 'border-primary bg-primary text-white'
                  : 'border-line-strong bg-panel text-ink-soft hover:border-primary hover:text-primary-dark'
              }`}
            >
              {tn.legalName}
            </button>
          ))}
        </div>
      )}

      {/* Filter toolbar */}
      <Card className="mb-4 p-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative min-w-[200px] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
              strokeWidth={1.75}
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('aiLog.searchPlaceholder')}
              aria-label={t('aiLog.searchPlaceholder')}
              className={`${inputCn} w-full pl-9`}
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-ink-soft">
            {t('aiLog.from')}
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={inputCn}
              aria-label={t('aiLog.from')}
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-ink-soft">
            {t('aiLog.to')}
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={inputCn}
              aria-label={t('aiLog.to')}
            />
          </label>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className={inputCn}
            aria-label={t('aiLog.userLabel')}
          >
            <option value="">{t('aiLog.allUsers')}</option>
            {knownUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setOnlyDecisions((v) => !v)}
            aria-pressed={onlyDecisions}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              onlyDecisions
                ? 'border-primary bg-primary/10 text-primary-dark'
                : 'border-line-strong text-ink-soft hover:border-primary'
            }`}
          >
            {t('aiLog.onlyDecisions')}
          </button>
        </div>
      </Card>

      {tenantId === '' ? null : isLoading ? (
        <div className="grid gap-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : isError ? (
        <EmptyState
          icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
          title={t('aiLog.error')}
        />
      ) : conversations.length === 0 && searching ? (
        // Never declare empty mid-drain: a match could still be sitting on an
        // unloaded page.
        <div className="grid gap-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <p className="text-center font-mono text-[11px] text-ink-faint">
            {t('aiLog.searchingAll', { count: all.items.length })}
          </p>
        </div>
      ) : conversations.length === 0 && filtersActive ? (
        <EmptyState
          icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
          title={t('aiLog.emptyFiltered')}
        />
      ) : conversations.length === 0 ? (
        <EmptyState
          icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
          title={t('aiLog.empty')}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]">
          {/* Conversation list */}
          <div className={`min-w-0 ${current ? 'hidden md:block' : ''}`}>
            {searching && (
              <p className="mb-2 font-mono text-[11px] text-ink-faint">
                {t('aiLog.searchingAll', { count: all.items.length })}
              </p>
            )}
            <div className="grid gap-2">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  type="button"
                  onClick={() => setSelected(conv.id)}
                  aria-current={conv.id === selected}
                  className={`min-w-0 overflow-hidden rounded-2xl border p-3.5 text-left transition-colors ${
                    conv.id === selected
                      ? 'border-primary bg-primary/5'
                      : 'border-line bg-panel hover:border-line-strong'
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <User className="h-3.5 w-3.5 flex-none text-primary" strokeWidth={2} />
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-ink">
                      {conv.userIds.map(userName).join(', ')}
                    </span>
                    <span className="ml-auto flex-none text-[11px] text-ink-faint">
                      {formatDate(conv.lastTs, '—')}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm text-ink-soft">{conv.preview}</p>
                  <div className="mt-2 flex items-center gap-1.5">
                    <Pill tone="slate">
                      {conv.turnCount}{' '}
                      {conv.turnCount === 1 ? t('aiLog.turnsOne') : t('aiLog.turnsMany')}
                    </Pill>
                    {conv.decisionCount > 0 && (
                      <Pill tone="amber">
                        {conv.decisionCount}{' '}
                        {conv.decisionCount === 1 ? t('aiLog.decisionOne') : t('aiLog.decisionMany')}
                      </Pill>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {capped && (
              <div className="mt-3 rounded-xl border border-line-strong bg-tone-amber-bg px-3.5 py-2.5 text-xs text-tone-amber">
                {t('aiLog.partialCoverage', { count: all.items.length })}
              </div>
            )}
            {all.nextCursor && (
              <div className="mt-3">
                {drainFailed && (
                  <p className="mb-2 text-xs font-semibold text-tone-red">{t('aiLog.error')}</p>
                )}
                <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
                  {loadingMore ? t('aiLog.loading') : t('aiLog.loadMore')}
                </Button>
              </div>
            )}
          </div>

          {/* Thread detail */}
          <div className={`min-w-0 ${current ? '' : 'hidden md:block'}`}>
            {current ? (
              <Card className="min-w-0 overflow-hidden p-4 sm:p-5">
                <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-line pb-3">
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="mr-1 rounded-lg p-1 text-ink-soft hover:bg-canvas md:hidden"
                    aria-label={t('aiLog.back')}
                  >
                    <ArrowLeft className="h-4 w-4" strokeWidth={2} />
                  </button>
                  <User className="h-4 w-4 text-primary" strokeWidth={2} />
                  <span className="text-sm font-bold text-ink">
                    {current.userIds.map(userName).join(', ')}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                    · {t('aiLog.conversation')} {current.id.slice(0, 8)}
                  </span>
                  <span className="ml-auto text-xs text-ink-faint">
                    {formatDate(current.lastTs, '—')}
                  </span>
                </div>
                <div className="grid gap-4">
                  {current.records.map((r) =>
                    r.recordType === 'turn' ? (
                      <TurnBubbles key={`${r.turnId}-${r.ts}`} turn={r} who={userName(r.userId)} />
                    ) : (
                      <DecisionRow
                        key={`${r.turnId ?? 'd'}-${r.ts}`}
                        decision={r}
                        who={userName(r.userId)}
                      />
                    )
                  )}
                </div>
              </Card>
            ) : (
              <EmptyState
                icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
                title={t('aiLog.pickOne')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Chat-style replay: the user's question as a right-aligned maroon bubble,
 * the copilot's answer as a left block — mirrors the Agente's visual language. */
function TurnBubbles({ turn, who }: { turn: AssistantTurnLogRecord; who: string }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-2">
      <div className="justify-self-end">
        <div className="mb-0.5 text-right font-mono text-[10px] uppercase tracking-wider text-ink-faint">
          {who} · {formatDate(turn.ts, '—')}
        </div>
        <div className="max-w-[46ch] break-words rounded-2xl rounded-tr-md bg-primary px-4 py-2.5 text-sm text-white">
          {turn.userMessage}
        </div>
      </div>
      <div className="min-w-0 justify-self-start">
        <div className="min-w-0 max-w-[70ch] rounded-2xl rounded-tl-md border border-line bg-canvas px-4 py-3">
          <ChatMarkdown text={turn.answer} />
          {(turn.toolsUsed.length > 0 || turn.modelId) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              {turn.toolsUsed.length > 0 && (
                <Wrench className="h-3.5 w-3.5 text-ink-faint" strokeWidth={1.75} />
              )}
              {turn.toolsUsed.map((tool) => (
                <Pill key={tool} tone="slate">
                  {tool}
                </Pill>
              ))}
              <span className="font-mono text-[10px] text-ink-faint">
                {t('aiLog.model')}: {turn.modelId}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DecisionRow({ decision, who }: { decision: AssistantDecisionLogRecord; who: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <Bot className="h-4 w-4 text-primary" strokeWidth={1.75} />
        <span className="text-sm font-bold text-ink">{t('aiLog.decision')}</span>
        <Pill tone={decision.editedFromProposal ? 'amber' : 'green'}>
          {decision.editedFromProposal ? t('aiLog.decisionEdited') : t('aiLog.decisionVerbatim')}
        </Pill>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-faint">
          {who} · {formatDate(decision.ts, '—')}
        </span>
      </div>
      <p className="text-sm text-ink-soft">
        {t('aiLog.decisionTool')}: <span className="font-mono text-xs">{decision.tool}</span>
        {decision.entity && (
          <>
            {' '}
            · {t('aiLog.decisionEntity')}:{' '}
            <span className="font-mono text-xs">
              {decision.entity.type} {decision.entity.id.slice(0, 8)}…
            </span>
          </>
        )}
      </p>
    </div>
  );
}
